import React, { useState } from "react";

import { Code, Typography } from "@packages/ds";
import { useIntl } from "react-intl";
import { useNavigate } from "react-router-dom";

import { CLI_COMMAND_NAME } from "Constants/documentationUrls";
import { Button, ButtonWrapper } from "ds/Button";
import useDecodedParams from "Hooks/useDecodedParams";
import useSelectorWithUrlParams from "Hooks/useSelectorWithUrlParams";
import getUrl from "Libs/getUrl";
import { formatBranchName, capitalize, goToEnvironment } from "Libs/utils";
import { branch, BranchError } from "Reducers/environment/actions/branch";
import { gitProjectSelector } from "Reducers/project/project";
import { useAppDispatch } from "Store/hooks";

import BranchInputs from "./BranchInputs";
import * as S from "./BranchPane.styles";
import ErrorContent from "./ErrorContent";

import type { Environment } from "@packages/client";

const ENVIRONMENT_LIMIT_ERROR_CODES = [402];

type BranchContentProps = {
  errors: BranchError;
  environment?: Environment;
  isLoading: boolean;
  modalTitle: React.ReactNode;
  cliDialog: React.ReactNode;
  hasEnvLimitError?: boolean;
};

const BranchContent = ({
  isLoading,
  errors,
  environment,
  modalTitle,
  cliDialog,
  hasEnvLimitError
}: BranchContentProps) => {
  const intl = useIntl();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const [branchName, setBranchName] = useState("");
  const [type, setType] = useState("development");

  const { organizationId, projectId, environmentId } = useDecodedParams<{
    organizationId: string;
    projectId: string;
    environmentId: string;
  }>();

  const onBranchNameChange = e => setBranchName(e.currentTarget.value);
  const onEnvironmentTypeChange = ({ value }) => setType(value);

  const project = useSelectorWithUrlParams(gitProjectSelector);

  const subscriptionId = project?.getSubscriptionId();

  const canEditPlan = project?.hasPermission("#subscription_change");

  const branchHandler = async () => {
    const response = await dispatch(
      branch({
        organizationId,
        projectId,
        environmentId,
        options: {
          branchName,
          type
        }
      })
    );

    if (response?.payload?.parameters?.environment) {
      const environmentOverviewUrl = getUrl({
        key: "organization.project.environment",
        props: {
          organizationId,
          projectId,
          environmentId: response.payload.parameters.environment
        }
      });
      navigate(environmentOverviewUrl);
    }
  };

  const command = intl.formatMessage(
    { id: "branch.cli.command" },
    {
      commandName: CLI_COMMAND_NAME,
      branchName: formatBranchName(branchName),
      environmentId,
      type: type === "development" ? "" : `--type ${type}`
    }
  );

  const successBtnText = intl.formatMessage({
    id: "branch.button"
  });

  const isEnvLimitReached =
    hasEnvLimitError ||
    ENVIRONMENT_LIMIT_ERROR_CODES.includes(errors?.code as number);

  const inputValues = {
    input: {
      label: intl.formatMessage({
        id: "branch.name.label"
      }),
      placeholder: intl.formatMessage({
        id: "branch.name.placeholder"
      })
    },
    dropdown: {
      label: intl.formatMessage({
        id: "environment_type"
      })
    }
  };

  return isEnvLimitReached ? (
    <ErrorContent
      project={project}
      organizationId={organizationId}
      projectId={projectId}
      environmentId={environmentId}
      subscriptionId={subscriptionId}
      canEditPlan={canEditPlan}
    />
  ) : (
    <>
      {modalTitle}
      <Typography.BodyText>
        {intl.formatMessage({ id: "branch.create.prompt" })}
      </Typography.BodyText>

      <BranchInputs
        branchName={branchName}
        onBranchNameChange={onBranchNameChange}
        onEnvironmentTypeChange={onEnvironmentTypeChange}
        environment={environment}
        inputValues={inputValues}
      />
      <S.Separator />

      <S.DialogWrapper>
        <Typography.Label textStyle="default">
          {intl.formatMessage({
            id: "branch.cli"
          })}
        </Typography.Label>
        {cliDialog}
      </S.DialogWrapper>

      <Code.Block isCopyable singleLine id="environment-branch-cmd">
        {command}
      </Code.Block>

      {isLoading ? (
        <S.Loading />
      ) : (
        <ButtonWrapper justifyContent="end" spacing="modal">
          <Button
            analyticId="cancel"
            id="environment-branch-modal-close-btn"
            variant="secondary"
            type="button"
            aria-label={intl.formatMessage({ id: "cancel" })}
            onClick={() =>
              goToEnvironment(
                navigate,
                organizationId,
                projectId,
                environmentId
              )
            }
          >
            {capitalize(intl.formatMessage({ id: "cancel" }))}
          </Button>
          <Button
            analyticId="branch.button"
            id="environment-branch-save-btn"
            type="submit"
            aria-label={successBtnText}
            disabled={!branchName}
            onClick={() => {
              if (branchName) {
                branchHandler();
              }
            }}
          >
            {capitalize(successBtnText)}
          </Button>
        </ButtonWrapper>
      )}
    </>
  );
};
export default BranchContent;
